import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { useUrlPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  StatusChip,
  PageViewerControls,
  KpiHeroBand,
  StatusDonutMini,
  KpiBandSkeleton,
} from '../../components/index.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { GlossaryTerm } from '../../components/primitives/GlossaryTerm.jsx';
import { usePeople } from '../../lib/usePeople.js';
import './accessReview.redesign.css';

// Chip labels for the active-filter bar (mirrors the AuditLogPage idiom).
const FILTER_LABELS = {
  requester: 'Requester',
  state: 'State',
  risk: 'Risk',
  fromDate: 'From',
  toDate: 'To',
};

// Plain-language label for an enum value (state / risk) in selects + chips —
// never surface a raw snake_case enum to the reviewer.
function prettyEnum(value) {
  if (!value) return '';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeDateInput(value, boundary) {
  if (!value) return null;
  const suffix = boundary === 'end' ? 'T23:59:59.999Z' : 'T00:00:00.000Z';
  const date = new Date(`${value}${suffix}`);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Terminal states never count as "pending" / "open" governance work.
const TERMINAL_STATES = new Set(['denied', 'withdrawn', 'expired', 'revoked']);
const HIGH_RISK = new Set(['high', 'critical', 'elevated']);

// SLA aging bands by days since last update (open requests only).
function agingTone(days, isOpen) {
  if (!isOpen) return 'slate';
  if (days == null) return 'slate';
  if (days >= 7) return 'brick';
  if (days >= 3) return 'amber';
  return 'sage';
}

function ageInDays(lastUpdatedAt) {
  if (!lastUpdatedAt) return null;
  const t = new Date(lastUpdatedAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

function riskTone(risk) {
  const r = String(risk || '').toLowerCase();
  if (HIGH_RISK.has(r)) return 'brick';
  if (r === 'moderate' || r === 'medium') return 'amber';
  if (r === 'standard' || r === 'low') return 'sage';
  return 'slate';
}

// Group the 15 workflow states into reviewer-meaningful buckets for the
// state-distribution rollup (kept honest: every item lands in exactly one).
function stateBucket(state) {
  const s = String(state || '').toLowerCase();
  if (s === 'denied' || s === 'expired' || s === 'revoked' || s === 'withdrawn')
    return { key: 'Closed/denied', tone: 'brick' };
  if (s === 'returned_for_rework') return { key: 'Returned', tone: 'amber' };
  if (s === 'active' || s === 'provisioned') return { key: 'Active', tone: 'sage' };
  if (s === 'recertification_due') return { key: 'Recert due', tone: 'amber' };
  if (s === 'draft') return { key: 'Draft', tone: 'slate' };
  return { key: 'In review', tone: 'blue' };
}

function buildSegments(items, classifier) {
  const counts = new Map();
  for (const it of items) {
    const { key, tone } = classifier(it);
    const prev = counts.get(key) || { label: key, count: 0, tone };
    prev.count += 1;
    counts.set(key, prev);
  }
  return [...counts.values()];
}

const EMPTY_FILTER = { requester: '', state: '', risk: '', fromDate: '', toDate: '' };

export function AccessRequestQueuePage() {
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 10 });
  const { nameOf } = usePeople();
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const r = useApi('/access-requests', {
    query: { limit: pager.pageSize, offset: pager.offset },
  });

  // The full fetched page, and the client-side filtered view (same pattern as
  // AuditLogPage): requester matches the raw id OR the resolved display name.
  const rawItems = useMemo(() => r.data?.data || [], [r.data]);
  const filtered = useMemo(() => {
    return rawItems.filter((req) => {
      if (filter.requester) {
        const needle = filter.requester.toLowerCase();
        const matches =
          String(req.requesterUserId || '')
            .toLowerCase()
            .includes(needle) || nameOf(req.requesterUserId).toLowerCase().includes(needle);
        if (!matches) return false;
      }
      if (filter.state && String(req.currentWorkflowState || '') !== filter.state) return false;
      if (
        filter.risk &&
        String(req.riskLevel || '').toLowerCase() !== filter.risk.toLowerCase()
      )
        return false;
      const updated = req.lastUpdatedAt ? new Date(req.lastUpdatedAt) : null;
      if (filter.fromDate) {
        const from = normalizeDateInput(filter.fromDate, 'start');
        if (from && (!updated || updated < from)) return false;
      }
      if (filter.toDate) {
        const to = normalizeDateInput(filter.toDate, 'end');
        if (to && (!updated || updated > to)) return false;
      }
      return true;
    });
  }, [rawItems, filter, nameOf]);

  // Only offer state / risk values that actually exist on this page.
  const stateOptions = useMemo(
    () => [...new Set(rawItems.map((it) => it.currentWorkflowState).filter(Boolean))].sort(),
    [rawItems],
  );
  const riskOptions = useMemo(
    () => [...new Set(rawItems.map((it) => it.riskLevel).filter(Boolean))].sort(),
    [rawItems],
  );

  const activeFilters = useMemo(
    () =>
      Object.entries(filter)
        .filter(([, v]) => v)
        .map(([key, value]) => ({
          key,
          label: FILTER_LABELS[key] || key,
          value: key === 'state' || key === 'risk' ? prettyEnum(value) : value,
        })),
    [filter],
  );

  const eyebrow = (
    <>
      Admin · <GlossaryTerm term="eSAAR" />
    </>
  );

  const filterControls = (
    <div className="row access-queue__filters">
      <input
        className="access-queue__filter"
        placeholder="requester…"
        aria-label="Requester"
        value={filter.requester}
        onChange={(e) => setFilter((f) => ({ ...f, requester: e.target.value }))}
      />
      <select
        className="access-queue__filter"
        aria-label="State"
        value={filter.state}
        onChange={(e) => setFilter((f) => ({ ...f, state: e.target.value }))}
      >
        <option value="">All states</option>
        {stateOptions.map((s) => (
          <option key={s} value={s}>
            {prettyEnum(s)}
          </option>
        ))}
      </select>
      <select
        className="access-queue__filter"
        aria-label="Risk"
        value={filter.risk}
        onChange={(e) => setFilter((f) => ({ ...f, risk: e.target.value }))}
      >
        <option value="">All risk</option>
        {riskOptions.map((rk) => (
          <option key={rk} value={rk}>
            {prettyEnum(rk)}
          </option>
        ))}
      </select>
      <input
        className="access-queue__filter"
        type="date"
        aria-label="From date"
        value={filter.fromDate}
        onChange={(e) => setFilter((f) => ({ ...f, fromDate: e.target.value }))}
      />
      <input
        className="access-queue__filter"
        type="date"
        aria-label="To date"
        value={filter.toDate}
        onChange={(e) => setFilter((f) => ({ ...f, toDate: e.target.value }))}
      />
    </div>
  );

  if (r.loading)
    return (
      <>
        <PageHeader title="Access Review Queue" eyebrow={eyebrow} />
        <KpiBandSkeleton count={3} />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (r.error) return <ErrorState error={r.error} />;
  const items = filtered;

  // Derive governance KPIs from the data the page already holds.
  const openItems = items.filter((it) => !TERMINAL_STATES.has(String(it.currentWorkflowState)));
  const openCount = openItems.length;
  const highRiskCount = items.filter((it) =>
    HIGH_RISK.has(String(it.riskLevel || '').toLowerCase()),
  ).length;
  const openAges = openItems.map((it) => ageInDays(it.lastUpdatedAt)).filter((d) => d != null);
  const oldestPendingAge = openAges.length ? Math.max(...openAges) : null;
  const oldestTone = agingTone(oldestPendingAge, true);

  const riskSegments = buildSegments(items, (it) => {
    const tone = riskTone(it.riskLevel);
    const label =
      tone === 'brick'
        ? 'High / critical'
        : tone === 'amber'
          ? 'Moderate'
          : tone === 'sage'
            ? 'Standard'
            : 'Unspecified';
    return { key: label, tone };
  });
  const stateSegments = buildSegments(items, (it) => stateBucket(it.currentWorkflowState));

  const kpiItems = [
    {
      id: 'open',
      label: 'Open reviews',
      value: openCount,
      status: openCount > 0 ? 'blue' : 'slate',
      statusLabel: openCount > 0 ? 'Active' : 'Clear',
      footer: `${items.length} on this page`,
    },
    {
      id: 'high-risk',
      label: 'High-risk requests',
      value: highRiskCount,
      status: highRiskCount > 0 ? 'brick' : 'sage',
      statusLabel: highRiskCount > 0 ? 'Escalate' : 'None',
      footer: highRiskCount > 0 ? 'Require senior sign-off' : 'No elevated risk',
    },
    {
      id: 'oldest-pending',
      label: 'Oldest pending',
      value: oldestPendingAge == null ? '—' : oldestPendingAge,
      unit: oldestPendingAge == null ? '' : oldestPendingAge === 1 ? 'day' : 'days',
      status: oldestTone,
      statusLabel:
        oldestPendingAge == null
          ? 'No open work'
          : oldestTone === 'brick'
            ? 'SLA breach'
            : oldestTone === 'amber'
              ? 'Watch'
              : 'On track',
      footer: 'Since last action',
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow={eyebrow}
        title="Access Review Queue"
        subtitle="All access requests across the 15-state workflow."
        actions={filterControls}
      />

      <KpiHeroBand
        eyebrow="Queue posture (this page)"
        asOf={r.loadedAt}
        items={kpiItems}
        columns={3}
        className="access-queue__command"
      />

      {rawItems.length > 0 ? (
        <div className="access-queue__filterbar" aria-label="Active filters and result count">
          {activeFilters.length ? (
            <>
              {activeFilters.map((f) => (
                <span key={f.key} className="access-queue__chip">
                  <span className="access-queue__chip-key">{f.label}:</span>
                  <span>{f.value}</span>
                  <button
                    type="button"
                    className="access-queue__chip-clear"
                    aria-label={`Clear ${f.label} filter`}
                    onClick={() => setFilter((prev) => ({ ...prev, [f.key]: '' }))}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="access-queue__filterbar-reset"
                onClick={() => setFilter(EMPTY_FILTER)}
              >
                Clear all
              </button>
            </>
          ) : (
            <span className="access-queue__rollup-sub">No active filters</span>
          )}
          <span className="access-queue__filterbar-count">
            <strong>{items.length}</strong> of {rawItems.length} on this page
          </span>
        </div>
      ) : null}

      {rawItems.length === 0 ? (
        <EmptyState title="No requests" />
      ) : items.length === 0 ? (
        <EmptyState
          title="No matching requests"
          description="No requests on this page match the current filters."
          action={
            <button type="button" className="btn" onClick={() => setFilter(EMPTY_FILTER)}>
              Clear all filters
            </button>
          }
        />
      ) : (
        <>
          <section className="access-queue__rollups access-queue__command">
            <div className="card">
              <div className="access-queue__rollup-head">
                <h2 className="card-title">Risk mix</h2>
                <span className="access-queue__rollup-sub">{items.length} requests</span>
              </div>
              <div className="access-queue__donut-wrap">
                <StatusDonutMini
                  segments={riskSegments}
                  size={108}
                  totalLabel="Requests"
                  ariaLabel="Risk distribution"
                />
              </div>
            </div>
            <div className="card">
              <div className="access-queue__rollup-head">
                <h2 className="card-title">eSAAR state distribution</h2>
                <span className="access-queue__rollup-sub">15-state workflow</span>
              </div>
              <div className="access-queue__donut-wrap">
                <StatusDonutMini
                  segments={stateSegments}
                  size={108}
                  totalLabel="Requests"
                  ariaLabel="Workflow state distribution"
                />
              </div>
            </div>
          </section>

          <table className="table access-queue__table">
            <thead>
              <tr>
                <th>Request</th>
                <th>Requester</th>
                <th>Risk</th>
                <th>State</th>
                <th>Aging</th>
                <th>Last update</th>
              </tr>
            </thead>
            <tbody>
              {items.map((req) => {
                const isOpen = !TERMINAL_STATES.has(String(req.currentWorkflowState));
                const days = ageInDays(req.lastUpdatedAt);
                const tone = isOpen ? agingTone(days, true) : 'resolved';
                const slaAttr = isOpen
                  ? tone === 'brick'
                    ? 'breach'
                    : tone === 'amber'
                      ? 'watch'
                      : 'ok'
                  : 'resolved';
                const dotTone = isOpen ? tone : 'slate';
                const ageText = days == null ? '—' : isOpen ? `${days}d open` : 'Resolved';
                const ageMeta = isOpen
                  ? slaAttr === 'breach'
                    ? 'SLA breach'
                    : slaAttr === 'watch'
                      ? 'Watch'
                      : 'On track'
                  : 'Closed';
                return (
                  <tr key={req.id} data-sla={slaAttr}>
                    <td>
                      <Link className="access-queue__id" to={`/admin/access-requests/${req.id}`}>
                        {req.id}
                      </Link>
                    </td>
                    <td>
                      <PersonLabel userId={req.requesterUserId} fallback="—" />
                    </td>
                    <td>
                      <StatusChip status={req.riskLevel} />
                    </td>
                    <td>
                      <StatusChip status={req.currentWorkflowState} />
                    </td>
                    <td>
                      <span className="access-queue__age">
                        <span className={`access-queue__age-dot tone-${dotTone}`} aria-hidden />
                        <span>
                          {ageText}
                          <span className="access-queue__age-meta"> · {ageMeta}</span>
                        </span>
                      </span>
                    </td>
                    <td>
                      {req.lastUpdatedAt ? new Date(req.lastUpdatedAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      <PageViewerControls
        page={r.data?.page}
        label="access requests"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}
